import { ARCA_OPERATOR_ROLES, formatCuit, isValidCuit } from './fiscalConnection';

export const ARCA_DELEGATION_GUIDE = Object.freeze({
  version: '2026-10-15.1',
  sourceUrl: 'https://www.arca.gob.ar/ws/WSAA/ADMINREL.DelegarWS.pdf',
  service: 'Web Services > Facturacion Electronica (wsfe)',
  roles: ARCA_OPERATOR_ROLES,
  steps: [
    {
      id: 'login', title: 'Ingresa al sitio oficial de ARCA',
      instruction: 'Abri ARCA en otra pestana e ingresa con la clave fiscal de la persona que realizara el tramite. Deja esta guia abierta para seguir los pasos.',
      warning: 'Esa persona debe ser Administrador o Subadministrador de Relaciones del CUIT del restaurante. No compartas la clave fiscal con MozzoApp ni la escribas aqui.',
      imageKey: 'arca-guide-step-login',
    },
    {
      id: 'relations', title: 'Abri el Administrador de Relaciones',
      instruction: 'Busca el servicio Administrador de Relaciones de Clave Fiscal. Segun tu acceso, puede aparecer como Subadministrador de Relaciones de Clave Fiscal.',
      warning: 'Si no aparece o no podes operar por el restaurante, consulta con su titular o contador. No uses el CUIT de otra persona para continuar.',
      imageKey: 'arca-guide-step-relations',
    },
    {
      id: 'represented', title: 'Elegi al restaurante como representado',
      instruction: 'Selecciona el CUIT que emitira las facturas y comparalo con los datos guardados que mostramos en esta guia.',
      warning: 'Si factura una sociedad, elegi el CUIT de esa sociedad, no el personal del administrador. Si factura una persona a su nombre, usa el CUIT de esa persona.',
      showSummary: true, imageKey: 'arca-guide-step-represented',
    },
    {
      id: 'new-relation', title: 'Crea una Nueva Relacion',
      instruction: 'Dentro del Administrador de Relaciones, selecciona Nueva Relacion. Esta opcion permite indicar expresamente al representado y al representante.',
      warning: 'Para este tramite no elijas Adherir Servicio: necesitamos autorizar a MozzoApp, no solo incorporar un servicio para uso propio.',
      imageKey: 'arca-guide-step-new-relation',
    },
    {
      id: 'service', title: 'Selecciona Facturacion Electronica',
      instruction: 'Busca la agrupacion Web Services de ARCA y, dentro de ella, Facturacion Electronica.',
      warning: 'No selecciones Comprobantes en linea. La vinculacion de MozzoApp utiliza el Web Service de Facturacion Electronica.',
      imageKey: 'arca-guide-step-service',
    },
    {
      id: 'representative', title: 'Indica el CUIT representante de MozzoApp',
      instruction: 'En CUIT/CUIL/CDI del usuario o Representante, ingresa el CUIT configurado para MozzoApp que figura en esta guia.',
      warning: 'Autoriza solamente el servicio de Facturacion Electronica. El restaurante sigue siendo el emisor de las facturas; MozzoApp opera el servicio tecnico autorizado.',
      imageKey: 'arca-guide-step-representative',
    },
    {
      id: 'review', title: 'Revisa los datos antes de confirmar',
      instruction: 'Compara el CUIT y la razon social del restaurante, el servicio elegido y el CUIT representante de MozzoApp con el resumen de abajo.',
      warning: 'Si algun dato no coincide, no confirmes. Volve al paso anterior dentro de ARCA y corregilo.',
      showSummary: true,
    },
    {
      id: 'confirm', title: 'Confirma la delegacion dentro de ARCA',
      instruction: 'Una vez revisados los datos, usa Confirmar en ARCA para finalizar la delegacion inicial.',
      warning: 'ARCA puede indicar que el autorizado tambien debe realizar una operacion. Es esperado: MozzoApp debe aceptar la designacion y asociar su computador fiscal.',
      imageKey: 'arca-guide-step-confirm',
    },
    {
      id: 'proof', title: 'Guarda la constancia F3283/E',
      instruction: 'Descarga o guarda la constancia que genera ARCA al confirmar. Conservala para la revision de la vinculacion.',
      warning: 'El F3283/E acredita la relacion o autorizacion realizada. No es una factura ni un CAE.',
      imageKey: 'arca-guide-step-proof',
    },
    {
      id: 'return', title: 'Volve a MozzoApp para continuar',
      instruction: 'Cuando hayas realizado el tramite real en ARCA, continua con Puntos de venta por sucursal y Revision y activacion, debajo de esta guia.',
      warning: 'Llegar al final de esta guia no informa la delegacion ni activa la facturacion. MozzoApp todavia debe revisar la autorizacion, completar su parte y verificar la conexion.',
    },
  ],
});

// Only add real, reviewed captures here. The guide never fabricates ARCA screens.
export const ARCA_DELEGATION_IMAGES = Object.freeze({});

export const hasSavedGuideIssuer = (issuer) => Boolean(
  issuer?.id && isValidCuit(issuer.cuit) && String(issuer.legalName || '').trim()
);

export const buildArcaAccountantInstructions = ({ issuer, representativeCuit, environment }) => {
  if (environment !== 'production' || !hasSavedGuideIssuer(issuer) || !isValidCuit(representativeCuit)) {
    return '';
  }
  return [
    'Vinculacion de Facturacion Electronica con MozzoApp - PRODUCCION',
    `Representado / emisor: ${String(issuer.legalName).trim()}`,
    `CUIT del restaurante: ${formatCuit(issuer.cuit)}`,
    `Representante tecnico MozzoApp: ${formatCuit(representativeCuit)}`,
    `Servicio: ${ARCA_DELEGATION_GUIDE.service}`,
    '',
    'El restaurante sigue siendo el emisor. Se autoriza a MozzoApp solamente a operar este servicio tecnico.',
    'El tramite lo realiza el Administrador o Subadministrador de Relaciones del CUIT emisor.',
    'Nunca compartas la clave fiscal. Ingresala exclusivamente en el sitio oficial de ARCA.',
    '',
    ...ARCA_DELEGATION_GUIDE.steps.map((step, index) => `${index + 1}. ${step.title}. ${step.instruction} ${step.warning}`),
    '',
    'No es una confirmacion de conexion activa. La aceptacion y la verificacion de MozzoApp son posteriores.',
    `Referencia oficial: ${ARCA_DELEGATION_GUIDE.sourceUrl}`,
  ].join('\n');
};
